// Family-aware syndrome criteria as data (no logic) so clinical staff can tune
// thresholds via PR without touching detection code. Disease matching is by
// name (case-insensitive) against the family's tracked diseases.
//
// A cancer named by its site (TGCLOUD-1847). SNOMED CT names a cancer by where
// it is and what it is: "Malignant neoplasm of breast", "Primary serous
// papillary cystadenocarcinoma of right ovary", "Adenocarcinoma of rectum".
// None of them contains "breast cancer", so a SNOMED-coded cancer counted
// towards no syndrome at all. A name counts by its site when it names the site
// (`cancerSites`), names a carcinoma or the cancer itself (CANCER_WORDS), and
// is none of NOT_THIS_CANCER.
//
// A SNOMED CT code is read by its site ONLY (syndromes.js): `diseasePatterns`
// took "Colorectal Crohn disease" for colorectal cancer. Any other name counts
// by `diseasePatterns` as it always did, or by its site.
//
// Measured against the SNOMED CT-AU hierarchy (release 20260930): of the
// primary carcinomas beneath "Malignant neoplasm of breast" and "of ovary" this
// counts 163, and misses the "by direct extension" and skin-of-breast codes it
// is meant to miss.
export const CANCER_WORDS =
  /carcinoma\b|\bcancer\b|\bmalignant (?:neoplasm|tumou?r)(?:,| of\b)|\bmalignant (?:epithelial|clear cell|serous|mucinous|endometrioid)\b/i;

export const NOT_THIS_CANCER = Object.freeze([
  /\bin situ\b/i,
  /\bbenign\b/i,
  // A secondary in the site. SNOMED CT names every one "Metastatic … TO
  // breast"; "Metastatic carcinoma OF breast" is a breast primary that has
  // spread, as is "Carcinoma of breast metastatic to bone".
  /\bsecondary\b/i,
  /^metasta\w*\s+(?:\S+\s+){0,4}?to\b/i,
  // Spread into the site from another organ.
  /\bby direct extension\b/i,
  // A cancer of the skin at the site.
  /\bskin\b/i,
  /syndrome|susceptib|predisposition/i,
  // Another kind of tumour at the site: the syndromes are of carcinomas.
  /sarcoma\b|lymphoma\b|melanoma\b|leuk(?:a)?emi|\bphyllodes\b|\bgerm cell\b|\bsex cord\b|\bgranulosa\b|\bsertoli\b|\bleydig\b|dysgerminoma|teratoma|\byolk sac\b|neuroendocrine|carcinoid|\bstromal\b|blastoma\b|\bmesenchymal\b|\bendocrine\b|\bislets? of langerhans\b|\bislet cell\b|\btheca\b|\bsteroid (?:producing )?cell\b|pseudopapillary/i
]);

const site = (words) => new RegExp(`\\b(?:${words})\\b`, 'i');

export const SYNDROME_CRITERIA = [
  {
    id: 'syndrome.hboc',
    label: 'Hereditary breast & ovarian cancer',
    omim: '604370',
    diseasePatterns: [/breast cancer/i, /ovarian cancer/i],
    cancerSites: [
      site('breasts?|nipple|areola'),
      site('ovary|ovaries|ovarian')
    ],
    genes: ['BRCA1', 'BRCA2'],
    guideline: { name: 'NCCN HBOC', version: '2024', criteriaId: null },
    minAffectedForStrong: 2
  },
  {
    id: 'syndrome.lynch',
    label: 'Lynch syndrome',
    omim: '120435',
    diseasePatterns: [/colorectal|colon cancer/i, /endometrial cancer/i],
    // The appendix is left out: it is not a Lynch cancer, though SNOMED CT
    // files it under the large intestine.
    cancerSites: [
      site(
        'colon|colonic|rectum|rectal|colorectal|colorectum|large intestine|large bowel|caecum|cecum|caecal|cecal|rectosigmoid|sigmoid|hepatic flexure|splenic flexure'
      ),
      site('endometrium|endometrial')
    ],
    genes: ['MLH1', 'MSH2', 'MSH6', 'PMS2', 'EPCAM'],
    guideline: {
      name: 'Amsterdam II / Bethesda',
      version: '—',
      criteriaId: null
    },
    minAffectedForStrong: 2
  }
];
